import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Play, Shield, Star, Sparkles, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

const toolkitFeatures = [
  "AI Content Monetisation Playbook",
  "27 AI Prompts",
  "90-Day Content Calendar",
  "Pricing Calculator",
  "Sales Page Templates",
  "Launch Email Swipe File",
];

const creatorPlanFeatures = [
  { text: "Everything in the Toolkit", included: true, highlight: false },
  { text: "Nexis AI — Script & video idea generator", included: true, highlight: true },
  { text: "Monetisation AI — Build digital products in one click", included: true, highlight: true },
  { text: "Sales Page Builder AI — Ready-to-publish sales pages", included: true, highlight: true },
  { text: "Email List Builder AI — Opt-in pages & sequences", included: true, highlight: true },
  { text: "Launch Advisor AI — 7-day launch plan", included: true, highlight: true },
  { text: "600 AI credits per month", included: true, highlight: true },
  { text: "Full video training course", included: true, highlight: true },
  { text: "Private community access", included: true, highlight: true },
  { text: "Monthly live Q&A calls", included: true, highlight: true },
];

const starterPlanFeatures = [
  "3 core AI tools",
  "200 AI credits per month",
  "Video training course",
  "Community access",
];

export default function Upgrade() {
  const [searchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [showDownsell, setShowDownsell] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const handleUpgrade = async (plan: "creator" | "starter" = "creator") => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-oto-checkout", {
        body: { email: searchParams.get("email") || "", plan },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast.error("Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  const handleDecline = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowDownsell(true);
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <Helmet>
        <title>Upgrade to Creator Plan | AI StoryCraft Studio</title>
        <meta name="description" content="Get the complete AI StoryCraft Studio Creator Plan — all 5 AI tools, 600 monthly credits, and full creator system at an exclusive annual price." />
      </Helmet>

      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-start justify-center pt-8">
          <div className="flex gap-2">
            {["🎉", "🎊", "✨", "🎉", "🎊"].map((e, i) => (
              <span key={i} className="text-3xl animate-bounce" style={{ animationDelay: `${i * 150}ms` }}>{e}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: 820 }}>
        {/* Success Header */}
        <Reveal>
          <div className="text-center pt-12 md:pt-16 pb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: "#ECFDF5", color: "#065F46" }}>
              <Check className="w-4 h-4" />
              <span className="text-sm font-semibold">Your Toolkit Purchase Was Successful! 🎉</span>
            </div>
            <p className="text-sm mb-8" style={{ color: "#6B7280" }}>Your AI Creator Monetisation Toolkit is ready to download. But before you access it...</p>
          </div>
        </Reveal>

        {/* Main Offer */}
        <Reveal delay={100}>
          <div className="text-center pb-8">
            <h1 className="text-2xl md:text-[2.5rem] font-extrabold leading-tight mb-4" style={{ color: "#1F2937" }}>
              Get the Complete AI Creator Studio —{" "}
              <span style={{ color: "#F59E0B" }}>4 Months Free</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#6B7280" }}>
              You just got the toolkit. Now unlock all 5 AI tools, 600 monthly credits, and the full creator system — at a price we'll never offer again.
            </p>
          </div>
        </Reveal>

        {/* Video */}
        <Reveal delay={200}>
          <div className="mb-10">
            <div className="w-full rounded-2xl overflow-hidden flex items-center justify-center cursor-pointer" style={{ aspectRatio: "16/9", background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
                <p className="text-white font-semibold text-sm">See what you get with the full system (2 minutes)</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Comparison */}
        <Reveal delay={300}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {/* Toolkit Card */}
            <div className="bg-white rounded-xl p-6" style={{ border: "1px solid #E5E7EB" }}>
              <h3 className="font-bold text-base mb-1" style={{ color: "#1F2937" }}>Your Toolkit ($17)</h3>
              <p className="text-xs mb-4" style={{ color: "#9CA3AF" }}>What you just purchased</p>
              <div className="space-y-2.5">
                {toolkitFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#10B981" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{f}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid #F3F4F6" }}>
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 flex-shrink-0" style={{ color: "#D1D5DB" }} />
                  <span className="text-sm" style={{ color: "#9CA3AF" }}>No AI tools access</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <X className="w-4 h-4 flex-shrink-0" style={{ color: "#D1D5DB" }} />
                  <span className="text-sm" style={{ color: "#9CA3AF" }}>No community or live calls</span>
                </div>
              </div>
            </div>

            {/* Creator Plan Card */}
            <div className="rounded-xl p-6 relative" style={{ border: "2px solid #7C3AED", background: "#FAFAFA" }}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: "#7C3AED" }}>
                RECOMMENDED
              </div>
              <h3 className="font-bold text-base mb-1" style={{ color: "#1F2937" }}>AI StoryCraft Studio Creator Plan (Annual)</h3>
              <p className="text-xs mb-4" style={{ color: "#7C3AED", fontWeight: 600 }}>Save $279 — 4 months free</p>
              <div className="space-y-2.5">
                {creatorPlanFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: f.highlight ? "#7C3AED" : "#10B981" }} />
                    <span className="text-sm" style={{ color: "#374151", fontWeight: f.highlight ? 600 : 400 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Pricing */}
        <Reveal delay={400}>
          <div className="text-center mb-4">
            <p className="text-sm mb-1" style={{ color: "#9CA3AF" }}>This one-time offer is only available on this page</p>
            <div className="mb-2">
              <p className="text-sm" style={{ color: "#6B7280" }}>
                Normally: <span className="line-through">$828/year</span> ($69/mo)
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-2xl line-through" style={{ color: "#9CA3AF" }}>$828</span>
              <span className="text-5xl font-extrabold" style={{ color: "#7C3AED" }}>$549<span className="text-lg font-bold">/year</span></span>
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: "#1F2937" }}>
              That's just $46/mo — <span style={{ color: "#F59E0B" }}>4 months completely free</span>
            </p>
            <p className="text-xs mb-6" style={{ color: "#6B7280" }}>Save $279 vs monthly billing</p>

            <button
              onClick={() => handleUpgrade("creator")}
              disabled={purchasing}
              className="w-full md:w-auto font-bold text-lg px-12 py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]"
              style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}
            >
              {purchasing ? "Processing..." : "Yes! Lock In My Annual Creator Plan → $549/year"}
            </button>

            <div className="mt-4">
              <button
                onClick={handleDecline}
                className="text-sm bg-transparent border-none cursor-pointer transition-colors hover:underline"
                style={{ color: "#9CA3AF" }}
              >
                No thanks, I'll just use the toolkit →
              </button>
            </div>
          </div>
        </Reveal>

        {/* Urgency */}
        <Reveal delay={450}>
          <p className="text-center text-xs mb-10" style={{ color: "#D97706" }}>⏰ This offer expires when you leave this page</p>
        </Reveal>

        {/* Testimonial */}
        <Reveal delay={500}>
          <div className="max-w-lg mx-auto mb-12">
            <div className="bg-white rounded-xl p-5" style={{ border: "1px solid #E5E7EB" }}>
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" style={{ color: "#F59E0B" }} />
                ))}
              </div>
              <p className="text-sm italic mb-3 leading-relaxed" style={{ color: "#374151" }}>
                "The full system helped me launch my first course and make $2,300 in the first month. The AI tools did 80% of the work — I just had to show up and follow the steps."
              </p>
              <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>Jake</p>
              <p className="text-xs" style={{ color: "#9CA3AF" }}>Finance YouTuber, 12K subs</p>
            </div>
          </div>
        </Reveal>

        {/* Guarantee */}
        <Reveal delay={600}>
          <div className="text-center mb-12">
            <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: "#7C3AED" }} />
            <h3 className="font-bold text-lg mb-2" style={{ color: "#1F2937" }}>30-Day Money-Back Guarantee</h3>
            <p className="text-sm max-w-lg mx-auto" style={{ color: "#6B7280" }}>
              If the full system doesn't help you monetise within 30 days, get a full refund. No questions asked.
            </p>
          </div>
        </Reveal>

        {/* Footer */}
        <footer className="py-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </footer>
      </div>

      {/* Downsell Modal */}
      {showDownsell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-2xl p-6 md:p-8 w-full relative" style={{ maxWidth: 520, boxShadow: "0 25px 50px rgba(0,0,0,0.15)" }}>
            <button
              onClick={() => { setShowDownsell(false); window.location.href = "/thank-you"; }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" style={{ color: "#9CA3AF" }} />
            </button>

            <div className="text-center mb-6">
              <p className="text-sm font-semibold mb-2" style={{ color: "#7C3AED" }}>WAIT — BEFORE YOU GO</p>
              <h2 className="text-xl md:text-2xl font-extrabold mb-3" style={{ color: "#1F2937" }}>
                Not ready for Creator?<br />
                Get <span style={{ color: "#F59E0B" }}>Starter</span> at 25% off.
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "#6B7280" }}>
                Lock in the annual Starter plan for just $349/year ($29/mo) — that's 25% less than monthly billing.
              </p>
            </div>

            <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" }}>
              <h4 className="font-semibold text-sm mb-3" style={{ color: "#1F2937" }}>Starter Plan includes:</h4>
              <div className="space-y-2">
                {starterPlanFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#10B981" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-lg line-through" style={{ color: "#9CA3AF" }}>$468/year</span>
              <span className="text-3xl font-extrabold" style={{ color: "#7C3AED" }}>$349<span className="text-sm font-bold">/year</span></span>
            </div>

            <button
              onClick={() => handleUpgrade("starter")}
              disabled={purchasing}
              className="w-full font-bold text-base py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]"
              style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}
            >
              {purchasing ? "Processing..." : "Get Starter Plan → $349/year"}
            </button>

            <button
              onClick={() => { setShowDownsell(false); window.location.href = "/thank-you"; }}
              className="w-full mt-3 text-sm bg-transparent border-none cursor-pointer transition-colors hover:underline text-center"
              style={{ color: "#9CA3AF" }}
            >
              No thanks, take me to my toolkit →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
